import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import { onAuthStateChanged, sendEmailVerification, Unsubscribe } from 'firebase/auth'
import { doc, onSnapshot } from 'firebase/firestore'
import { auth, firestore, socket } from '..'
import Loading from '../views/loading'
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import makeToast from '../components/toast'

interface AuthContext {
  loading: boolean
  user: UserExtended | null
  currentRole: string
  availableSpeechTranslationFiles: string
  availableWebInterfaceTranslationFiles: string
  availableIBTGTranslationFiles: string
  companies: string
  company: string
  serialNumbers: string
  manufacturer: string
  multiFactorAuthentication: boolean
  embossersNotifications: string
  usersNotifications: string
  firstTimeSignIn: boolean
  currentTimeZoneOffset: number
  emailVerified: boolean
  signOut: () => void
  setLoading: Dispatch<SetStateAction<boolean>>
  setAvailableSpeechTranslationFiles: Dispatch<SetStateAction<string>>
  setAvailableWebInterfaceTranslationFiles: Dispatch<SetStateAction<string>>
  setAvailableIBTGTranslationFiles: Dispatch<SetStateAction<string>>
  setCompanies: Dispatch<SetStateAction<string>>
  setCompany: Dispatch<SetStateAction<string>>
  setSerialNumbers: Dispatch<SetStateAction<string>>
  setManufacturer: Dispatch<SetStateAction<string>>
  setMultiFactorAuthentication: Dispatch<SetStateAction<boolean>>
  setEmbossersNotifications: Dispatch<SetStateAction<string>>
  setUsersNotifications: Dispatch<SetStateAction<string>>
  setUser: Dispatch<SetStateAction<UserExtended | null>>
  setFirstTimeSignIn: Dispatch<SetStateAction<boolean>>
}

export interface UserExtended {
  uid: string
  availableSpeechTranslationFiles: string
  availableWebInterfaceTranslationFiles: string
  availableIBTGTranslationFiles: string
  companies: string
  company: string
  displayName: string
  email: string
  emailVerified: boolean
  embossersNotifications: string
  firstTimeSignIn: boolean
  manufacturer: string
  multiFactorAuthentication: boolean
  phoneNumber: string
  photoURL: string
  removed: 'false' | 'true'
  role: string
  serialNumbers: string
  signedIn: boolean
  theme: 'light' | 'dark'
  usersNotifications: string
}

const Auth = createContext({} as AuthContext)

const emailVerificationDialogStyles = {
  backgroundColor: "#f6f6f6",
  borderRadius: "10px",
  boxShadow: "0px 3px 12px -1px rgba(0, 0, 0, 0.2)"
}

export default function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState<boolean>(false)
  const [user, setUser] = useState<UserExtended | null>(null)
  const [currentRole, setCurrentRole] = useState<string>('None')
  const [firstTimeSignIn, setFirstTimeSignIn] = useState<boolean>(true)
  const [availableSpeechTranslationFiles, setAvailableSpeechTranslationFiles] = useState<string>('')
  const [availableWebInterfaceTranslationFiles, setAvailableWebInterfaceTranslationFiles] = useState<string>('')
  const [availableIBTGTranslationFiles, setAvailableIBTGTranslationFiles] = useState<string>('')
  const [company, setCompany] = useState<string>('')
  const [companies, setCompanies] = useState<string>('')
  const [serialNumbers, setSerialNumbers] = useState<string>('')
  const [manufacturer, setManufacturer] = useState<string>('')
  const [multiFactorAuthentication, setMultiFactorAuthentication] = useState<boolean>(false)
  const [embossersNotifications, setEmbossersNotifications] = useState<string>('')
  const [usersNotifications, setUsersNotifications] = useState<string>('')
  const [dialog, setDialog] = useState<boolean>(false)
  const [emailVerified, setEmailVerified] = useState<boolean>(false)
  const [currentTimeZoneOffset, setCurrentTimeZoneOffset] = useState<number>(0)

  let signOut: () => void = () => {
    socket.emit('set_signed_in', user!.uid, false, multiFactorAuthentication)
    auth.signOut().then(() => {
      console.log('User signed out successfully.')
      setUser(null)
      setLoading(false)
    }).catch((error: any) => {
      console.error('An error has occured while trying to sign out.')
      setLoading(false)
    })
  };

  useEffect(() => {
    // eslint-disable-next-line
    signOut = () => {
      try {
        socket.emit('set_signed_in', user!.uid, false, multiFactorAuthentication)
      } finally {
        auth.signOut().then(() => {
          console.log('User signed out successfully.')
          setUser(null)
          setLoading(false)
        }).catch((error: any) => {
          console.error('An error has occured while trying to sign out.')
          setLoading(false)
        })
      }
    }
  }, [user?.uid, multiFactorAuthentication])

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(() => {
        const currentDate = new Date()
        setCurrentTimeZoneOffset(currentDate.getTimezoneOffset() * 60000)
      }, () => {
        console.error("Unable to retrieve current coordinates.");
      })
    } else {
      console.error("Unable to retrieve current coordinates.");
    }
  }, [])

  useEffect(() => {
    let unsubAuth: Unsubscribe
    let unsubSnapshot: Unsubscribe
    let userData: UserExtended | null = null

    unsubAuth = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        userData = {
          uid: authUser!.uid,
          availableSpeechTranslationFiles: '',
          availableWebInterfaceTranslationFiles: '',
          availableIBTGTranslationFiles: '',
          companies: '',
          company: '',
          displayName: authUser!.displayName || '',
          email: authUser!.email!,
          emailVerified: authUser!.emailVerified || false,
          embossersNotifications: 'on',
          firstTimeSignIn: true,
          manufacturer: '',
          multiFactorAuthentication: false,
          phoneNumber: authUser!.phoneNumber || '',
          photoURL: authUser!.photoURL || '',
          removed: 'false',
          role: 'None',
          serialNumbers: '',
          signedIn: false,
          theme: 'light',
          usersNotifications: 'on'
        }

        await auth.currentUser?.getIdToken(true)
        const {
          claims: {
            email_verified: emailVerified
          }
        } = await auth.currentUser!.getIdTokenResult()

        if (!emailVerified) {
          setDialog(true)
        }

        unsubSnapshot = onSnapshot(doc(firestore, 'users', userData!.uid), async (snapshot) => {
          if (snapshot.exists()) {
            await auth.currentUser?.getIdToken(true)
            const {
              claims: {
                email_verified: emailVerified,
                role: currentUsersRole
              }
            } = await auth.currentUser!.getIdTokenResult()
    
            if (currentUsersRole === "Blocked") {
              setUser(null)
              makeToast('error', 'Your account has been blocked!', 'light')
              signOut()
              return
            }
    
            if (snapshot.data().removed === 'true') {
              setUser(null)
              makeToast('error', 'Your account has been removed.', 'light')
              signOut()
              return
            }
    
            setCurrentRole(currentUsersRole === '' || currentUsersRole === undefined || currentUsersRole === null ? "None" : currentUsersRole as string)
    
            if (!emailVerified) {
              // Notify the user that he needs to verify his email address
              setEmailVerified(false)
            } else {
              setEmailVerified(true)
            }

            if (snapshot.data().firstTimeSignIn === false) {
              setFirstTimeSignIn(false)
            } else {
              setFirstTimeSignIn(true)
            }

            if (userData?.role !== snapshot.data().role && !(currentUsersRole === "Global Administrator")) {
              makeToast('success', 'Your role has been updated.', snapshot.data().theme)
            }

            userData = {
              uid: snapshot.id,
              ...snapshot.data(),
            } as UserExtended

            if (userData.photoURL === '' && auth.currentUser!.photoURL !== '') {
              socket.emit('set_profile_picture', userData!.uid, auth.currentUser!.photoURL || "")
            }
            if (userData.signedIn === false && userData !== null) {
              socket.emit('set_signed_in', userData.uid, true, userData!.multiFactorAuthentication)
            }
            setUser(userData)
            setMultiFactorAuthentication(userData!.multiFactorAuthentication)
            setAvailableSpeechTranslationFiles(userData!.availableSpeechTranslationFiles)
            setAvailableWebInterfaceTranslationFiles(userData!.availableWebInterfaceTranslationFiles)
            setAvailableIBTGTranslationFiles(userData!.availableIBTGTranslationFiles)
            setCompanies(userData!.companies)
            setCompany(userData!.company)
            setSerialNumbers(userData!.serialNumbers)
            setManufacturer(userData!.manufacturer)
            setEmbossersNotifications(userData!.embossersNotifications)
            setUsersNotifications(userData!.usersNotifications)
            setLoading(false)
          }
        })
      } else {
        unsubSnapshot && unsubSnapshot()
        setUser(null)
      }
    })

    return () => {
      unsubAuth && unsubAuth()
      unsubSnapshot && unsubSnapshot()
      signOut()
    }
    // eslint-disable-next-line
  }, [])

  return (
    <Auth.Provider
      value={{
        loading,
        user,
        currentRole,
        availableSpeechTranslationFiles,
        availableWebInterfaceTranslationFiles,
        availableIBTGTranslationFiles,
        companies,
        company,
        serialNumbers,
        manufacturer,
        multiFactorAuthentication,
        embossersNotifications,
        usersNotifications,
        firstTimeSignIn,
        currentTimeZoneOffset,
        emailVerified,
        signOut,
        setLoading,
        setAvailableSpeechTranslationFiles,
        setAvailableWebInterfaceTranslationFiles,
        setAvailableIBTGTranslationFiles,
        setCompanies,
        setCompany,
        setSerialNumbers,
        setManufacturer,
        setMultiFactorAuthentication,
        setEmbossersNotifications,
        setUsersNotifications,
        setUser,
        setFirstTimeSignIn
      }}
    >
    {
      !loading ?
        <div>
          {children}
          <Dialog
            open={dialog}
            PaperProps={{
              style: emailVerificationDialogStyles
            }}
            onClose={() => setDialog(false)}
            fullWidth
            maxWidth="xs"
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title" sx={{ color: "black" }}>
              Email not verified!
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description" sx={{ color: "black" }}>
                You need to verify your email to be able to receive access to other pages.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
            <Button
                onClick={() => {
                  sendEmailVerification(auth.currentUser!)
                  setDialog(false)
                }}
                size="large"
                sx={{
                  fontWeight: "bold",
                  color: "#85d201"
                }}
              >
                Send Verification Mail
              </Button>
              <Button
                onClick={() => setDialog(false)}
                size="large"
                sx={{
                  fontWeight: "bold",
                  color: "#85d201"
                }}
              >
                Ok
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      :
        <Loading />
    }
    </Auth.Provider>
  )
}

export const useAuth = () => useContext(Auth)
